import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plane, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setErr(""); setBusy(true);
    try { await login(email.trim(), password); nav("/"); }
    catch (ex) { setErr(apiError(ex, "Login failed")); }
    finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-slate-950">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-grid border-r border-slate-800 relative overflow-hidden">
        <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-indigo-600/20 blur-3xl" />
        <div className="flex items-center gap-3 relative">
          <div className="h-10 w-10 rounded-md bg-indigo-600 grid place-items-center shadow-[0_0_20px_rgba(99,102,241,0.4)]"><Plane className="h-5 w-5" /></div>
          <div>
            <div className="font-display font-bold text-lg leading-none">Tramint Ops</div>
            <div className="text-xs text-slate-500">Travel Sales & Payment Desk CRM</div>
          </div>
        </div>
        <div className="relative space-y-6 max-w-lg">
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-[1.05]">Inbound sales, split billing and the Payment Desk — one command center.</h1>
          <p className="text-slate-400">Agents capture bookings and card authorizations, the Payment Desk charges on the right merchant, and management sees every dollar of MCO in real time.</p>
          <div className="grid grid-cols-3 gap-4 pt-4">
            {[["AES-256", "field encryption"], ["RBAC", "3 role tiers"], ["Audit", "every card view"]].map(([a, b]) => (
              <div key={a} className="panel p-4"><div className="font-mono font-bold text-indigo-300">{a}</div><div className="text-xs text-slate-500 mt-1">{b}</div></div>
            ))}
          </div>
        </div>
        <div className="text-xs text-slate-600 relative">© {new Date().getFullYear()} Tramint Travel · Internal use only</div>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 fade-up" data-testid="login-form">
          <div>
            <div className="eyebrow mb-2">Sign in</div>
            <h2 className="font-display text-2xl font-bold">Welcome back</h2>
            <p className="text-sm text-slate-500 mt-1">Use your agency credentials to continue.</p>
          </div>
          <div className="space-y-3">
            <label className="block text-sm">
              <span className="text-slate-400">Email</span>
              <input data-testid="login-email-input" className="field mt-1" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
            </label>
            <label className="block text-sm">
              <span className="text-slate-400">Password</span>
              <input data-testid="login-password-input" className="field mt-1" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </label>
          </div>
          {err && <div data-testid="login-error" className="text-sm text-rose-400 border border-rose-500/30 bg-rose-500/10 rounded-md px-3 py-2">{err}</div>}
          <Button data-testid="login-submit-btn" type="submit" disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-500">
            <Lock className="h-4 w-4 mr-2" /> {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
